import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/** Support contact behind "Support & Feedback". */
export const HUMAI_SUPPORT_HREF =
  'mailto:eric.fritzsche@pmi.com?subject=ComPlan%20%E2%80%93%20Support%20%26%20Feedback';
/** Opens a 1:1 Microsoft Teams chat (Teams app or web). */
export const HUMAI_TEAMS_HREF = 'https://teams.microsoft.com/l/chat/0/0?users=eric.fritzsche@pmi.com';

/**
 * The "made by humai." badge, fixed bottom right on every page. A click fades in
 * the info pop-up; a click outside, Escape or the badge again closes it.
 *
 * Rendered once in `App`, outside the router, so it also shows on the session
 * skeleton and the error screens. Styles live in `/assets/humai/humai.css`
 * (loaded from `index.html`), which the server-rendered password sign-in page
 * shares. It sits below dialogs and the Detail_Panel drawer, so it never covers
 * their action buttons.
 */
export function HumaiBadge(): React.JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const popId = useId();

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent): void => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        setOpen(false);
        trigger.current?.focus();
      }
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="humai" ref={root}>
      {open && (
        <section className="humai-pop" id={popId} aria-label={t('humai.popupLabel')}>
          <img className="humai-logo" src="/assets/humai/logo.svg" alt={t('humai.logoAlt')} />
          <p className="humai-claim">
            <strong>{t('humai.claim')}</strong>
            <br />
            <span>{t('humai.tagline')}</span>
          </p>
          <hr className="humai-line" />
          <p className="humai-credits">
            {t('humai.credits')}
            <br />
            <span className="humai-links">
              <a href={HUMAI_SUPPORT_HREF}>
                <img src="/assets/humai/support.svg" alt="" aria-hidden="true" />
                {t('humai.support')}
              </a>
              <a href={HUMAI_TEAMS_HREF} target="_blank" rel="noopener noreferrer">
                <img src="/assets/humai/chat.svg" alt="" aria-hidden="true" />
                {t('humai.teams')}
              </a>
            </span>
          </p>
        </section>
      )}
      <button
        ref={trigger}
        type="button"
        className="humai-trigger"
        aria-expanded={open}
        aria-controls={open ? popId : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        <img src="/assets/humai/footer.svg" alt={t('humai.badgeAlt')} />
      </button>
    </div>
  );
}
